import React, { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../../shared/layouts/MainLayout';
import { Card, Alert, Loading, Badge, Spinner } from '../../../shared/components';
import { useReports } from '../hooks/useReports';
import { useExport } from '../hooks/useExport';
import ReportCharts from '../components/ReportCharts';
import ExportButtons from '../components/ExportButtons';
import ReportTable from '../components/ReportTable';
import ReportFilters from '../components/ReportFilters';

// Rank medal emojis for top items
const rankMedals = {
  1: '🥇',
  2: '🥈',
  3: '🥉'
};

const TopConsumedPage = () => {
  const [filters, setFilters] = useState({ 
    period: 'monthly',
    startDate: '',
    endDate: '',
    limit: 10
  });
  
  const { data, loading, error, refetch } = useReports('top_consumed', filters);
  const exportUtils = useExport();

  // Debug: Log the actual data structure
  useEffect(() => {
    console.log('TopConsumedPage - Full data from API:', data);
    if (data) {
      console.log('TopConsumedPage - data.data:', data.data);
      console.log('TopConsumedPage - data.data.data:', data.data?.data);
      console.log('TopConsumedPage - Is data.data.data an array?', Array.isArray(data.data?.data));
    }
  }, [data]);

  // Extract the actual data array from the nested structure
  const getRawDataArray = () => {
    if (!data) return [];
    
    // Based on your API response structure
    if (data.data && data.data.data && Array.isArray(data.data.data)) {
      return data.data.data;
    }
    
    // Fallback: try data.data if it's an array
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Fallback: try data itself if it's an array
    if (Array.isArray(data)) {
      return data;
    }
    
    return [];
  };

  const rawData = getRawDataArray();

  // Calculate statistics
  const stats = useMemo(() => {
    if (!rawData.length) return { totalItems: 0, totalConsumption: 0, topItem: null };
    
    const totalConsumption = rawData.reduce((sum, item) => 
      sum + parseInt(item.total_used), 0
    );
    
    const topItem = rawData.length > 0 ? rawData[0] : null;
    
    return { 
      totalItems: rawData.length, 
      totalConsumption,
      topItem
    };
  }, [rawData]);

  const columns = [
    { 
      header: 'Rank', 
      accessor: 'rank',
      render: (value, row, index) => {
        const rank = index + 1;
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono text-gray-500">#{rank}</span>
            {rank <= 3 && (
              <span className="text-xl" role="img" aria-label={`rank ${rank}`}>
                {rankMedals[rank]}
              </span>
            )}
          </div>
        );
      }
    },
    { 
      header: 'Medicine Name', 
      accessor: 'name',
      render: (value, row, index) => {
        const rank = index + 1;
        return (
          <div className="flex items-center gap-2">
            <span className={rank <= 3 ? 'font-bold text-gray-900' : ''}>
              {value}
            </span>
            {rank === 1 && <Badge variant="success">Most Consumed</Badge>}
          </div>
        );
      }
    },
    { 
      header: 'Total Quantity Used', 
      accessor: 'total_used',
      render: (value, row, index) => {
        const rank = index + 1;
        const percentage = stats.totalConsumption > 0 
          ? ((parseInt(value) / stats.totalConsumption) * 100).toFixed(1)
          : 0;
        
        return (
          <div className="flex items-center gap-2">
            <span className={rank <= 3 ? 'font-bold text-blue-600' : ''}>
              {parseInt(value).toLocaleString()}
            </span>
            <span className="text-xs text-gray-500">
              ({percentage}%)
            </span>
          </div>
        );
      }
    },
    {
      header: 'Usage Trend',
      accessor: 'trend',
      render: (value, row, index) => {
        const rank = index + 1;
        if (rank === 1) {
          return <Badge variant="success">↑ High Usage</Badge>;
        } else if (rank === 2) {
          return <Badge variant="warning">→ Moderate</Badge>;
        } else if (rank === 3) {
          return <Badge variant="info">↓ Low</Badge>;
        }
        return <span className="text-gray-400">-</span>;
      }
    }
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    refetch();
  };

  const handleReset = () => {
    setFilters({
      period: 'monthly',
      startDate: '',
      endDate: '',
      limit: 10
    });
    refetch();
  };

  // Handler functions for exports
  const handleCSVExport = () => {
    exportUtils.exportToCSV(rawData, 'top_consumed');
  };

  const handleExcelExport = () => {
    exportUtils.exportToExcel(rawData, 'top_consumed');
  };

  const handlePDFExport = () => {
    exportUtils.exportToPDF(
      rawData, 
      columns, 
      'Top Consumed Medicines Report', 
      'top_consumed'
    );
  };

  const handlePrint = () => {
    exportUtils.print();
  };

  if (loading) return <Spinner />;
  if (error) return <Alert type="error" message={error} />;

  return (
    // <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Top Consumed Medicines</h1>
        
        {/* Filters */}
        <ReportFilters
          filters={filters}
          onApply={handleApplyFilters}
          onReset={handleReset}
        />
        
        {/* Statistics Cards */}
        {rawData.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Total Medicines</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.totalItems}</p>
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Total Consumption</h3>
                <p className="text-2xl font-bold text-green-600">
                  {stats.totalConsumption.toLocaleString()} units
                </p>
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Top Medicine</h3>
                <p className="text-xl font-bold text-purple-600">
                  {stats.topItem?.name || '-'}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.topItem?.total_used} units used
                </p>
              </div>
            </Card>
          </div>
        )}
        
        {/* Export Buttons */}
        <ExportButtons
          onExportCSV={handleCSVExport}
          onExportExcel={handleExcelExport}
          onExportPDF={handlePDFExport}
          onPrint={handlePrint}
          disabled={loading}
          showRecordCount={true}
          recordCount={rawData.length}
        />
        
        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Table Card */}
          <Card>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Top Consumed List</h2>
                <Badge variant="info">{filters.period} period</Badge>
              </div>
              
              {rawData.length > 0 ? (
                <ReportTable
                  data={{ data: rawData }}
                  columns={columns}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No consumption data available</p>
                </div>
              )}
            </div>
          </Card>
          
          {/* Charts Card */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">Consumption Distribution</h2>
              {rawData.length > 0 ? (
                <>
                  <ReportCharts
                    data={rawData}
                    type="top_consumed"
                    title="Top Medicines by Usage"
                  />
                  
                  {/* Top 3 Highlights */}
                  <div className="mt-6 space-y-3">
                    <h3 className="text-sm font-medium text-gray-700">Top 3 Medicines</h3>
                    {rawData.slice(0, 3).map((item, index) => {
                      const percentage = ((parseInt(item.total_used) / stats.totalConsumption) * 100).toFixed(1);
                      return (
                        <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                          <span className="text-2xl">{rankMedals[index + 1]}</span>
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{item.name}</span>
                              <span className="text-sm font-semibold text-blue-600">
                                {item.total_used} units
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div 
                                className="bg-blue-600 h-2 rounded-full" 
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500">{percentage}% of total</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No chart data available</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Insights Section */}
        {rawData.length > 0 && (
          <Card className="mt-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Usage Insights</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Most Consumed Insight */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Most Consumed</h4>
                  <p className="text-sm text-blue-600">
                    <span className="font-bold">{rawData[0]?.name}</span> is the most consumed medicine 
                    with {rawData[0]?.total_used} units used, representing{' '}
                    {((parseInt(rawData[0]?.total_used) / stats.totalConsumption) * 100).toFixed(1)}% 
                    of total consumption.
                  </p>
                </div>

                {/* Consumption Pattern */}
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-2">Consumption Pattern</h4>
                  <p className="text-sm text-green-600">
                    The top 3 medicines account for{' '}
                    {((rawData.slice(0, 3).reduce((sum, item) => sum + parseInt(item.total_used), 0) / 
                      stats.totalConsumption) * 100).toFixed(1)}% of total usage.
                  </p>
                </div>

                {/* Period Comparison */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-2">Period Analysis</h4>
                  <p className="text-sm text-purple-600">
                    {filters.period === 'monthly' ? 'This month' : 
                     filters.period === 'weekly' ? 'This week' : 
                     filters.period === 'yearly' ? 'This year' : 'This period'}, 
                    total consumption was <span className="font-bold">{stats.totalConsumption} units</span>.
                  </p>
                </div>

                {/* Recommendation */}
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h4 className="font-medium text-orange-800 mb-2">Stock Recommendation</h4>
                  <p className="text-sm text-orange-600">
                    Consider increasing stock levels for top consumed medicines to 
                    prevent stockouts, especially for {rawData[0]?.name}.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    // </MainLayout>
  );
};

export default TopConsumedPage;